import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Sparkles, Zap, Gauge, Layers, Infinity as InfinityIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Navbar, Footer } from '../components/Layout';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = {
  trial: { hex: '#94a3b8', glass: 'glass-tint', icon: Sparkles },
  plus: { hex: '#22d3ee', glass: 'glass-tint glass-cyan', icon: Zap },
  pro: { hex: '#a78bfa', glass: 'glass-tint glass-violet', icon: Gauge },
  max: { hex: '#34d399', glass: 'glass-tint glass-emerald', icon: Layers },
  elite: { hex: '#fbbf24', glass: 'glass-tint glass-amber', icon: InfinityIcon },
};

// What each level is for. Capacity is the ladder, not which parts of FuturePath you may see.
const INCLUDED = {
  trial: ['Broad access to the FuturePath experience', 'Patricia, your AI guide',
    'Setting up your profile never costs credits', 'Everything you create stays yours after the trial'],
  plus: ['Every FuturePath module, nothing is locked away', 'Patricia, career and college intelligence',
    'Lifestyle Design and your income floor', 'Readiness, skills, funding and your report',
    'Ongoing planning at a comfortable pace'],
  pro: ['Everything in Plus, with more than twice the capacity',
    'Room to compare options properly before you commit', 'More documents, scenarios and reports each cycle',
    'Faster handling when the platform is busy'],
  max: ['Everything in Pro, at four and a half times Plus capacity',
    'Deep research and multi-option analysis without rationing',
    'Higher file, scenario and report limits', 'Priority processing'],
  elite: ['Everything in Max, at more than eleven times Plus capacity',
    'For several goals or people moving at once', 'Highest limits across the platform',
    'Top priority processing and early access to new intelligence'],
};

// Outcome-based capacity language. The exact credit figure stays inside the app, on the usage
// meter, nobody should be counting down from a number on a marketing page.
const CAPACITY = {
  plus: 'For steady, ongoing planning, explore every module at a comfortable pace.',
  pro: 'For people actively making decisions right now, go deep without slowing down.',
  max: 'For frequent, heavier research and multi-option analysis, built so you never have to ration it.',
  elite: 'For continuous, always-on planning support at the highest capacity we offer.',
};

export default function PricingPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState('');
  const [trialVisible, setTrialVisible] = useState(false);

  useEffect(() => {
    axios.get(`${API}/plans`).then(r => {
      setPlans(r.data.plans || []); setPacks(r.data.credit_packs || []);
      setTrialVisible(!!r.data.trial_visible);
    })
      .catch(() => toast.error('Could not load plans just now.'));
  }, []);

  const paid = useMemo(() => plans.filter(p => p.key !== 'trial'), [plans]);
  const trial = plans.find(p => p.key === 'trial');

  const start = async (planKey) => {
    // Not signed in? Go straight to sign-up and payment for the plan they picked,     // no role picker in between.
    if (!user) {
      navigate(`/register/student?plan=${planKey}&interval=${annual ? 'annual' : 'monthly'}`);
      return;
    }
    setBusy(planKey);
    try {
      const r = await axios.post(`${API}/subscription/checkout`,
        { plan: planKey, interval: annual ? 'annual' : 'monthly', origin_url: window.location.origin },
        getAuthHeader());
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open checkout.');
      setBusy('');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1729]">
      <Navbar />

      <section className="relative pt-32 pb-16 px-6" data-testid="pricing-hero">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight">
            Choose the FuturePath that fits how you plan
          </motion.h1>
          <p className="text-base md:text-lg text-slate-300/90 mt-6 max-w-2xl mx-auto leading-relaxed">
            One intelligent platform for career, education, skills, finances and major life transitions.
            Choose the level of AI capacity that fits your journey.
          </p>

          <div className="inline-flex items-center gap-1 mt-10 p-1 rounded-full border border-white/12 bg-white/[0.04]"
            data-testid="billing-toggle">
            {[['Monthly', false], ['Annual', true]].map(([label, isAnnual]) => (
              <button key={label} onClick={() => setAnnual(isAnnual)}
                data-testid={`toggle-${label.toLowerCase()}`}
                className="px-5 py-2 rounded-full text-sm transition-all duration-200"
                style={annual === isAnnual
                  ? { background: 'linear-gradient(90deg,#22d3ee,#a78bfa)', color: '#04121a' }
                  : { color: '#cbd5e1' }}>
                {label}
              </button>
            ))}
          </div>
          <AnimatePresence>
            {annual && (
              <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="text-sm text-emerald-300 mt-4" data-testid="annual-savings">
                Save two months · billed annually upfront
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Trial */}
      {trial && trialVisible && (
        <section className="px-6 pb-8">
          <div className="max-w-6xl mx-auto glass-tint fp-rail p-6 md:p-7 flex flex-wrap items-center gap-6"
            data-testid="trial-card">
            <div className="flex-1 min-w-[260px]">
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-300">Start here</p>
              <h2 className="font-outfit text-2xl font-semibold text-white mt-1.5">
                {trial.trial_days}-day free trial · {trial.trial_credits} AI credits
              </h2>
              <p className="text-sm text-slate-300/85 mt-2">
                No credit card required. Setting up your profile does not use credits, and everything you
                create stays accessible when the trial ends.
              </p>
            </div>
            <Link to={user ? '/app/dashboard' : '/pricing'} data-testid="trial-cta">
              <Button className="rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/15">
                Start free trial <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* The ladder */}
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {paid.map((p, i) => {
            const a = ACCENT[p.key] || ACCENT.plus;
            const Icon = a.icon;
            const price = annual ? p.price_annual : p.price_monthly;
            const isCurrent = user?.plan === p.key;
            return (
              <motion.div key={p.key} initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className={`${a.glass} p-6 flex flex-col relative`}
                style={p.badge ? { borderColor: `${a.hex}66`, boxShadow: `0 0 60px -20px ${a.hex}` } : undefined}
                data-testid={`plan-card-${p.key}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[11px] font-semibold"
                    style={{ background: a.hex, color: '#0b1220' }}>{p.badge}</span>
                )}
                <Icon className="w-6 h-6" style={{ color: a.hex }} />
                <h3 className="font-outfit text-2xl font-semibold text-white mt-3">{p.name}</h3>

                <div className="mt-4">
                  <span className="font-outfit text-4xl font-bold text-white fp-num" data-testid={`price-${p.key}`}>
                    ${price?.toLocaleString()}
                  </span>
                  <span className="text-sm text-slate-300">{annual ? '/year' : '/month'}</span>
                  {annual && (
                    <p className="text-xs text-slate-300 mt-1">
                      ${p.effective_monthly_on_annual}/month equivalent · billed annually upfront
                    </p>
                  )}
                </div>

                <div className="mt-5 pb-5 border-b border-white/10">
                  <p className="text-sm text-slate-200/90" data-testid={`capacity-${p.key}`}>
                    {CAPACITY[p.key] || 'Generous monthly AI capacity.'}
                  </p>
                  <p className="text-xs text-slate-300 mt-1.5" title={`${p.monthly_credits?.toLocaleString()} AI credits every month, including on annual billing`}
                    data-testid={`credits-${p.key}`}>
                    Generous monthly AI capacity · <a href="#capacity-explainer" className="underline decoration-dotted hover:text-slate-300">what that means</a>
                  </p>
                </div>

                <ul className="mt-5 space-y-2.5 flex-1">
                  {(INCLUDED[p.key] || []).map(f => (
                    <li key={f} className="flex gap-2.5 text-sm text-slate-300/85">
                      <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: a.hex }} />{f}
                    </li>
                  ))}
                </ul>

                <Button onClick={() => start(p.key)} disabled={busy === p.key || isCurrent}
                  data-testid={`select-${p.key}`}
                  className="w-full mt-6 rounded-full text-slate-900 font-semibold"
                  style={{ background: `linear-gradient(90deg, ${a.hex}, #22d3ee)` }}>
                  {isCurrent ? 'Your current plan' : busy === p.key ? 'Opening checkout…' : `Choose ${p.name}`}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-sm text-slate-300 mt-10 max-w-3xl mx-auto">
          Every paid plan includes every FuturePath module. The ladder answers how much FuturePath can
          work for you, not which parts you are allowed to see. Capacity refreshes every monthly cycle,
          and re-opening something you already ran never costs you anything.
        </p>
      </section>

      {/* What capacity means, in plain language, never a per-action price list */}
      <section className="px-6 pb-20">
        <div id="capacity-explainer" className="max-w-5xl mx-auto glass-tint glass-cyan fp-rail p-7 md:p-8" data-testid="capacity-explainer">
          <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-white">
            How AI capacity works
          </h2>
          <p className="text-sm text-slate-300/85 mt-3 max-w-2xl leading-relaxed">
            Credits are simply how we measure how much AI work your plan includes. Conversations and
            recommendations use very little; full written reports and deep multi-option analysis use more.
            FuturePath chooses the right level of intelligence for each task automatically, you never
            manage models, settings or limits.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-6">
            {[['Plenty of room', 'Plus covers months of steady planning, conversations, matching, scenarios and reports.'],
              ['Nothing is rationed', 'Every module is included on every paid plan. Capacity is the only difference.'],
              ['Top up any time', 'If a month runs long, add capacity in $10 steps without changing plans.']].map(([t, b]) => (
              <div key={t} className="fp-tile p-4">
                <p className="text-sm font-semibold text-white">{t}</p>
                <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-300 mt-5">
            Setting up your profile, your readiness score, ROI maths and funding matches never use credits.
          </p>
        </div>
      </section>

      {/* Top-ups */}
      {packs.length > 0 && (
        <section className="px-6 pb-24">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-outfit text-xl font-semibold text-white text-center">
              Need more in a single month?
            </h2>
            <p className="text-sm text-slate-300 text-center mt-2">
              Add capacity in $10 steps. Top-ups apply to your current cycle and are used only after
              your monthly allowance.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-7" data-testid="credit-packs">
              {packs.map(pk => (
                <div key={pk.key} className="fp-tile p-5 text-center" data-testid={`pack-${pk.key}`}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-300">{pk.label}</p>
                  <p className="font-outfit text-2xl font-bold text-white mt-2 fp-num">
                    {pk.credits.toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-300">credits</p>
                  <p className="text-lg text-cyan-300 mt-3 fp-num">${pk.price_usd}</p>
                  {pk.note && <p className="text-[11px] text-amber-300/90 mt-2">{pk.note}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
